/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Opaque from "App/render/webgl/nodes/bins/opaque_bin";
import {vec3} from "gl-matrix";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";
import RenderBinNode from "App/render/webgl/nodes/render_bin_node";
import Factory from "App/render/webgl/nodes/bins/factory";
import {PointsBinTypeName} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class Points extends Opaque {

    public static TypeName: string = PointsBinTypeName;
    private _pgc: { camPos: vec3 };

    constructor(aSceneAPI: IScene, aRenderer: WebGLRenderGraph) {
        super(aSceneAPI, aRenderer);

        this._pgc = {
            camPos: vec3.create()
        };
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): Points {
        return new Points(aSceneAPI, aRenderGraph);
    }

    public setUniforms(aRenderPass: Pass) {
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let view = aRenderPass.view;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        let framebuffer = this._renderGraph.getActiveFramebuffer();

        RenderBinNode.prototype.setUniforms.call(this, aRenderPass);

        let fov = view.getCamera().getFieldOfView();
        let size = view.getSize();
        let heightOfNearPlane = fov > 0 ? Math.abs(size[0] - size[1]) / (2 * Math.tan(0.5 * fov * Math.PI / 180.0)) : 0.0;

        let scale = framebuffer.getParameters().scale;
        gl.uniform1f(shader.uniformLocations.u_height_of_near_plane, heightOfNearPlane);
        gl.uniform2fv(shader.uniformLocations.u_viewport, size);
        gl.uniform1f(shader.uniformLocations.u_viewport_scale, scale);

        let camPosition = view.getCamera().getCameraPosition();
        let camTarget = view.getCamera().getCameraTarget();
        let dir = view.getCamera().getCameraDirection();

        dir[1] = 0.0;

        let dist = vec3.distance(camPosition, camTarget);
        vec3.scaleAndAdd(this._pgc.camPos, camTarget, dir, -dist);

        gl.uniformMatrix4fv(
            shader.uniformLocations.u_look_forward_projection,
            false,
            view.getCamera().getCustomViewProjectionMatrix(this._pgc.camPos, camTarget));
    }

    public render(aRenderPass: Pass): boolean {
        aRenderPass.bindShader(this._typeName);

        this.setUniforms(aRenderPass);

        RenderBinNode.prototype.render.call(this, aRenderPass);

        aRenderPass.unbindShader();

        return true;
    }
}

Factory.register(Points);
